import React, { useEffect, useMemo, useState } from "react";
import { CustomDialog } from "../../../components/StyledComponents";
import { DialogActions, DialogContent, DialogTitle } from "@mui/material";
import {
    ControlpanelPrimaryButton,
    ControlpanelSecondaryButton,
    GrayIconButton,
} from "../../../components/StyledButton";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { useTranslation } from "react-i18next";
import CustomInputHorizontal from "../../../components/CustomInputHorizontal";
// import { updateRecipient } from "../../../axios/Setting/RecipientsApi"; // TODO: dùng API thật
import { toast } from "react-toastify";
import { editRecipients } from "../../../axios/Setting/SettingUrlApi";
import { useLoading } from "../../../context/LoadingContext";
import CustomInputHorizontal1 from "../../../components/CustomInputHorizontal1";

const EditRecipientsDialog = ({ open, handleClose, data, onSave, onRefresh }) => {
    const { t } = useTranslation();
    const [show, hide] = useLoading();

    const [formData, setFormData] = useState({
        id: "",
        email: "",
        remark: "",
    });

    const [validationMessages, setValidationMessages] = useState({
        email: "",
        remark: "",
    });

    useEffect(() => {
        if (!open || !data) return;
        setFormData({
            id: data.id ?? "",
            email: data.email ?? "",
            remark: data.remark ?? "",
        });
        setValidationMessages({ email: "", remark: "" });
    }, [open, data]);

    const emailRegex = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/, []);

    const validateField = (name, value, data = formData) => {
        setValidationMessages((prev) => {
            const next = { ...prev };

            if (name === "email") {
                if (!value.trim()) {
                    next.email = t("common.required");
                } else if (!emailRegex.test(value.trim())) {
                    next.email = t("validation.email-invalid-format");
                } else {
                    next.email = "";
                }
            }

            if (name === "remark") {
                if (!value.trim()) {
                    next.remark = t("common.required");
                } else {
                    next.remark = "";
                }
            }

            return next;
        });
    };

    const validate = (d = formData) => {
        const v = { email: "", remark: "" };

        if (!d.email?.trim()) v.email = t("common.required");
        else if (!emailRegex.test(d.email.trim())) v.email = t("validation.email-invalid-format");

        if (!d.remark?.trim()) v.remark = t("common.required");

        setValidationMessages(v);
        return !v.email && !v.remark;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const next = { ...formData, [name]: value };
        setFormData(next);
        validateField(name, value, next);
    };

    const canSave = useMemo(() => {
        const emailOk = formData.email.trim() && emailRegex.test(formData.email.trim());
        const remarkOk = !!formData.remark.trim();
        return emailOk && remarkOk;
    }, [formData, emailRegex]);

    const handleSave = async () => {
        if (!validate()) {
            toast.warning(t("validation.fill-required-fields"));
            return;
        }
        const payload = {
            id: formData.id,
            email: formData.email.trim(),
            remark: formData.remark.trim(),
        };
      
        show();
        try {
            const response = await editRecipients(payload);
            if (
                response.data.message === "Successful" ||
                response.data.message === "Thành công"
            ) {
                handleClose?.(true);
                toast.success(t("common.edit-recipient-successfully"));
                onRefresh();
                hide();
            } else if (response.data.responseCode === 1085) {
                handleClose?.(true);
                toast.warning(response.data.message);
                onRefresh();
                hide();
            } else {
                hide();
                toast.error(response.data.message);
            }
        } catch (error) {
            // setLoading(false);
            if (error.response.data.responseCode === 1085) {
                handleClose?.(true);
                toast.warn(error.response.data.message);
                onRefresh();
                hide();
            } else {
                hide();
                toast.error(error.response.data.message);
            }
        }

        handleClose?.(true);
    };

    return (
        <CustomDialog maxWidth="sm" open={open} onClose={() => handleClose?.(false)} keepMounted>
            <DialogTitle>
                <div className="flex justify-between">
                    <div className="flex items-center gap-4">
                        <p className="text-xl font-semibold">
                            {t("common.edit-send-mail-to-another-email-address")}
                        </p>
                    </div>
                    <GrayIconButton onClick={() => handleClose?.(false)} className="self-start">
                        <CloseIcon />
                    </GrayIconButton>
                </div>
            </DialogTitle>

            <DialogContent>
                <div className="grid grid-cols-1 gap-x-10 gap-y-3 relative">
                    <CustomInputHorizontal1
                        required
                        title={t("common.email")}
                        inputType="text"
                        name="email"
                        id="email"
                        placeholder={t("common.email")}
                        message={validationMessages.email}
                        handleInputChange={handleInputChange}
                        value={formData.email}
                    />
                    {validationMessages.email && (
                        <p className="text-[14px] text-red-500 mt-1 pl-[17%]">{validationMessages.email}</p>
                    )}

                    <CustomInputHorizontal1
                        required
                        title={t("common.remark")}
                        inputType="text"
                        name="remark"
                        id="remark"
                        placeholder={t("common.remark")}
                        message={validationMessages.remark}
                        handleInputChange={handleInputChange}
                        value={formData.remark}
                    />
                    {validationMessages.remark && (
                        <p className="text-[14px] text-red-500 mt-1 pl-[17%]">{validationMessages.remark}</p>
                    )}
                </div>
            </DialogContent>

            <DialogActions>
                <div className="flex gap-2 px-4 py-4">
                    <ControlpanelSecondaryButton onClick={() => handleClose?.(false)}>
                        <p>{t("button.cancel")}</p>
                    </ControlpanelSecondaryButton>
                    <ControlpanelPrimaryButton onClick={handleSave} disabled={!canSave}>
                        <p>{t("button.save")}</p>
                    </ControlpanelPrimaryButton>
                </div>
            </DialogActions>
        </CustomDialog>
    );
};

export default EditRecipientsDialog;
